import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import MarkdownWithMermaid from '../common/MarkdownWithMermaid';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import SectionHeader from '../atoms/Header';
import LoadMoreButton from '../atoms/LoadMore';
import ConfirmationDialog from '../common/ConfirmationDialog';
import useDynamicSections from '../../hooks/useDynamicSections';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useNotification from '../../hooks/useNotification';
import EmptyContentPlaceholder from '../atoms/EmptyContentPlaceholder';
import { EditableTextField } from '../atoms';
import useExplain from '../../hooks/useExplain';
import ExplanationModal from '../atoms/ExplanationModal';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import ImprovementModal from '../atoms/ImprovementModal';
import mermaid from 'mermaid';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}

/**
 * A split-view layout component that shows conversation on the left
 * and dynamically rendered version control content on the right with accordion sections
 */
const SplitViewVersionControl = ({ conversation, children, isStreaming, streamingContent, projectId }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { showNotification } = useNotification();
  const versionControlPanelRef = useRef(null);

  // Use the common hooks for dynamic sections
  const {
    sections,
    setSections,
    expandedSections,
    setExpandedSections,
    visibleSections,
    handleSectionToggle,
    handleLoadMore,
    contentRef,
    hasContent,
    isMatchingConversation: isVersionControlConversation
  } = useDynamicSections(conversation, isStreaming, streamingContent, 'version-control');

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);


  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use CRUD operations hook
  const {
    editingSection,
    editedContent,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setDeleteConfirmation,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleDeleteSection,
    handleDeleteSectionConfirmed,
    handleImproveSection,
    handleCopySection
  } = useSectionCRUD(conversation, sections, setSections, setExpandedSections, contentRef);

  // Custom explain handler that uses the real explain functionality
  const handleExplainSection = (section) => {
    requestExplanation(section);
  };

  // Improve functionality
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport,
    handleCopy,
    handleRefreshDiagrams,
    handleSaveToDocument,
    getExportContent,
  } = useContentActions(contentRef, 'Version Control', projectId, conversation?._id);

  // Full screen functionality
  const { 
    isFullScreen, 
    toggleFullScreen, 
    getLeftPanelStyles, 
    getRightPanelStyles,
    getContentStyles 
  } = useFullScreen();
  
  // Initialize validation state for sections
  const initialValidationState = sections.reduce((acc, section) => {
    acc[section.id] = false;
    return acc;
  }, {});
  
  // Use the validation hook
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSection,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'version-control',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'version-control',
  });

  console.log('SplitViewVersionControl rendering', {
    conversationId: conversation?._id,
    taskId: conversation?.taskId,
    isVersionControlConversation,
    isStreaming
  });

  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && versionControlPanelRef.current && isVersionControlConversation) {
      setTimeout(() => {
        if (versionControlPanelRef.current) {
          versionControlPanelRef.current.scrollTop = versionControlPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [isStreaming, isVersionControlConversation]);

  // Don't show the split view if this isn't a version control conversation or if there's no content
  
  if (!isVersionControlConversation || (!hasContent && !isStreaming)) {
    console.log('Not showing split view: either not version control conversation or no content available');
    return <>{children}</>;
  }

  console.log('Showing split view with version control content', { hasContent, isStreaming });

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Version control panel */}
        <Box
          ref={versionControlPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
              <SectionHeader label='Version control' />
              {/* Fixed header section */}
              <TopBarButtons
                isFullScreen={isFullScreen}
                onToggleFullScreen={toggleFullScreen}
                getExportContent={getExportContent}
                  onExport={handleExport}
                isExporting={isExporting}
                onCopy={handleCopy}
                onRefresh={handleRefreshDiagrams}
                onSave={projectId ? () => handleSaveToDocument('VERSION_CONTROL') : undefined}
                isSaving={isSaving || isSavingDocument}
                disabled={!contentRef.current}
                exportTooltip="Export version control as markdown"
                copyTooltip="Copy version control to clipboard"
                refreshTooltip="Refresh diagrams"
                saveTooltip="Save as document"
              />
            </Box>

            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'version-control'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={sections?.length || 0}
              currentUserMemberType={currentUserMemberType}
            />

            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show version control content or a placeholder if none found yet */}
              {isStreaming && sections.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography color="text.secondary">
                    Generating version control content...
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </Box>
              ) : sections.length > 0 ? (
                <Box>
                  {/* Render sections as accordions */}
                  {sections.slice(0, visibleSections).map((section, index) => (
                    <React.Fragment key={section.id}>
                    <Accordion
                      ref={shouldHighlight(section.id) ? highlightRef : null}
                      expanded={expandedSections[section.id] || false}
                      onChange={() => handleSectionToggle(section.id)}
                      sx={{
                        mb: 3,
                        '&.Mui-expanded': {
                          margin: 0,
                        }
                      }}
                    >
                      <AccordionSummary
                        sx={{
                          backgroundColor: '#f5f8fb',
                          borderRadius: '4px',
                          position: 'relative',
                          margin: 0,
                          minHeight: 0,
                          padding: '10px',
                          '&.Mui-expanded': {
                            margin: 0,
                            minHeight: 0,
                          },
                          '& .MuiAccordionSummary-expandIconWrapper': {
                            display: 'none',
                          },
                          '& .MuiAccordionSummary-content': {
                            margin: 0,
                          },
                          '& .MuiAccordionSummary-content.Mui-expanded': {
                            margin: 0,
                          }
                        }}
                      >
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'space-between'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px", textTransform: 'none !important' }}>
                            {`${index + 1}. ${section.title}`}
                          </Typography>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            flexDirection: "column",
                            justifyContent: "center"
                          }}>
                            <Box onClick={async (e) => {
                              e.stopPropagation();
                              const isCurrentlyValidated = validatedSections[section.id];
                              
                              try {
                                await handleValidateSection(section.id);
                                
                                // Show notification based on validation state change
                                if (isCurrentlyValidated) {
                                  showNotification({
                                    message: `Validation removed for "${section.title}"`,
                                    type: "error"
                                  });
                                } else {
                                  showNotification({
                                    message: `"${section.title}" validated successfully`,
                                    type: "success"
                                  });
                                }
                              } catch (error) {
                                showNotification({
                                  message: `Failed to update validation for "${section.title}"`,
                                  type: "error"
                                });
                              }
                            }}
                              sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}>
                              {validatedSections[section.id] ? (
                                <ValidatedIcon size={25} memberType={getValidatorType(section.id)} />
                              ) : (
                                <UnvalidatedIcon size={25} />
                              )}
                            </Box>
                           
                            <Box
                              sx={{
                                display: 'flex',
                                transform: expandedSections[section.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </Box>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ 
                        p: 2,
                        backgroundColor: '#ffffff',
                        position: 'relative',
                        cursor: editingSection === section.id ? 'default' : 'pointer'
                      }}
                      onClick={() => {
                        if (!editingSection && editingSection !== section.id) {
                          handleStartEdit(section.id, section.content || '');
                        }
                      }}>
                        <Box sx={{
                          width: "93%",
                          minHeight: "160px"
                        }}>
                          {/* Show edit mode or content */}
                          {editingSection === section.id ? (
                            // Edit mode
                            <EditableTextField
                              value={editedContent}
                              onChange={(e) => setEditedContent(e.target.value)}
                            />
                          ) : (
                            // Normal content display
                           <Box
                            ref={getSectionContentRef(section.id)}
                            sx={{ position: "relative" }}
                          >
                            <MarkdownWithMermaid content={section.content} />
                            <TextSelectionPopup
                           containerRef={getSectionContentRef(section.id)}
                           sectionId={section.id}
                           onSubmitComment={addComment}
                           disabled={editingSection === section.id}

                           />
                          </Box>
                          )}
                        </Box>
                        {/* Action buttons */}
                        <Box sx={{
                          position: 'absolute',
                          right: 10,
                          top: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5
                        }}>
                          <ActionButtons
                            item={section}
                            itemIndex={index}
                            featureId={section.id}
                            editingFeature={editingSection}
                            isSaving={isSaving}
                            onEdit={() => handleStartEdit(section.id, section.content || '')}
                            onSave={() => handleSaveEdit(section.id)}
                            onCancel={handleCancelEdit}
                            onImprove={() => handleImproveClick(section)}
                            onExplain={() => handleExplainSection(section)}
                            onCopy={() => handleCopySection(section)}
                            onDelete={() => handleDeleteSection(section)}
                            vertical={true}
                          onComment={() => openCommentPanel(section.id)}
                          commentCount={commentCounts[section.id]?.open || 0}
                          isApproved={validatedSections[section.id]}
                          approvedByType={validatedSections[section.id] ? getValidatorType(section.id) : null}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                    {showImproveInput === section.id && (
                      <InlineImproveInput
                        improvePrompt={improvePrompt}
                        onPromptChange={setImprovePrompt}
                        onSend={() => handleSendImprove(section)}
                        onCancel={handleCancelImprove}
                      />
                    )}
                    </React.Fragment>
                  ))}

                  {/* Load more button for lazy loading */}
                  {sections.length > visibleSections && (
                    <LoadMoreButton
                      remaining={sections.length - visibleSections}
                      onLoadMore={handleLoadMore}
                      label="Load more sections"
                    />
                  )}
                </Box>
              ) : (
                <EmptyContentPlaceholder
                  title="No version control content found yet."
                  description="Ask the AI to create comprehensive version control guidelines for your project. You can use prompts like:"
                  examplePrompt="Can you create Git branching strategies, commit conventions, and code review guidelines?"
                  additionalInfo="You can also ask for specific Git workflows like GitFlow, GitHub Flow, or trunk-based development strategies."
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
      
      {/* Confirmation dialog for delete */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        onConfirm={handleDeleteSectionConfirmed}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height='auto'
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (sections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewVersionControl;
